import { Alert, Offcanvas, Stack, Table } from 'react-bootstrap';
import { IDocumentSearchModel, IViewDocumentModel } from '../../interfaces/IDocument';
import { useEffect, useState } from 'react';
import {
    useGetMyDocuments,
    useGetMyDocumentsCount,
    useGetMyDocumentsKey,
} from './useGetMyDocuments';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import { AppliedFiltersSection } from '../../components/shared/filters/AppliedFiltersSection';
import { CheckmateDialog } from '../../components/shared/dialog';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import DocumentRow from './DocumentRow';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import { DocumentsHeader } from './DocumentsHeader';
import { ISortState } from '../../interfaces/ISortState';
import { Loader } from '../../components/shared/Loader';
import { MyDocumentsAppliedFilters } from './MyDocumentsAppliedFilters';
import MyDocumentsSearchFilters from './MyDocumentsSearchFilters';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import Pagination from '../../shared/Pagination';
import Sort from '../../stores/Sort';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../shared/ToastUtils';
import { useCollabReportDeleteMutation } from './useCollabReportDeleteMutation';
import { useDocumentTypesLookup } from '../../shared/react-query-hooks/useDocumentTypesLookup';
import { useMyDocumentDeleteMutation } from './useMyDocumentDeleteMutation';
import { useQueryClient } from 'react-query';
import { useZoneContext } from '../../contexts/useZoneContext';

const pageTitle = 'My Documents';
const defaultSearchModel: IDocumentSearchModel = {};

export function MyDocumentsPage() {
    const queryClient = useQueryClient();
    const { zoneGuid } = useZoneContext();

    const [sort, setSort] = useState<ISortState | null>(null);
    const [error, setError] = useState<string | undefined>(undefined);
    const [filteredRows, setFilteredRows] = useState<IViewDocumentModel[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [pageSize] = useState<number>(100);
    const [deleteCallback, setDeleteCallback] = useState<() => void | undefined>();

    const [documentsDownloading, setDocumentsDownloading] = useState<string[]>([]);
    const [documentsWithErrors, setDocumentsWithErrors] = useState<string[]>([]);
    const [documentSearchModel, setDocumentSearchModel] =
        useState<IDocumentSearchModel>(defaultSearchModel);
    const [showFilterPane, setShowFilterPane] = useState<boolean>(false);

    const myDocumentsApi = useGetMyDocuments(documentSearchModel, pageNumber, pageSize);
    const { data: documentsCountResponse, refetch: refetchDocumentsCount } =
        useGetMyDocumentsCount(documentSearchModel);
    const [documentToDelete, setDocumentToDelete] = useState<IViewDocumentModel | null>(null);

    const documentTypesLookup = useDocumentTypesLookup();

    const deleteMutation = useMyDocumentDeleteMutation((errorMessage: string) =>
        setError(errorMessage)
    );

    const deleteCollabReportMutation = useCollabReportDeleteMutation((errorMessage: string) =>
        setError(errorMessage)
    );

    const downloadDocument = async (
        event: React.FormEvent<HTMLButtonElement>,
        documentGuid: string,
        documentName?: string
    ): Promise<void> => {
        setError('');
        event.preventDefault();
        const xhr = new XMLHttpRequest();
        const url = '/' + ApiRoutes.MyDocumentsDownload.replace('{id}', documentGuid);

        setDocumentsDownloading((prev) => [...prev, documentGuid]);
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Content-Type', 'application/msword');
        xhr.setRequestHeader('X-Tenant-Guid', zoneGuid);
        return new Promise((resolve, reject) => {
            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                    let fileName = documentName ? documentName : 'EntityDocument.doc';
                    const blob = xhr.response;

                    const contentDisposition = xhr.getResponseHeader('Content-Disposition');
                    if (contentDisposition) {
                        const contentDispositionItems = contentDisposition.split(';');
                        if (contentDispositionItems) {
                            for (let i = 0; i < contentDispositionItems.length; i++) {
                                const currentItem = contentDispositionItems[i].trim();
                                if (currentItem.includes('filename=')) {
                                    fileName = currentItem
                                        .replace('filename=', '')
                                        .replace(/"/g, '');
                                    break;
                                }
                            }
                        }
                    }
                    const a = document.createElement('a');
                    a.href = window.URL.createObjectURL(blob);
                    a.download = fileName;
                    a.dispatchEvent(new MouseEvent('click'));

                    resolve();
                }

                if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 400) {
                    setError(DisplayMessages.DocumentDownloadError);
                    return reject(new Error(DisplayMessages.DocumentDownloadError));
                }

                setDocumentsDownloading((prev) =>
                    prev.filter((docGuid) => docGuid !== documentGuid)
                );
            };

            // Handle potential network errors
            xhr.onerror = () => {
                return reject(new Error('Network error during download'));
            };

            xhr.send();
        });
    };

    const executeDelete = (document: IViewDocumentModel) => {
        setError(undefined);

        const handleDeleteSuccess = () => {
            setDocumentToDelete(null);
            myDocumentsApi.refetch();
            refetchDocumentsCount();

            if (deleteCallback) {
                deleteCallback();
            }
        };

        if (document.isCollab) {
            deleteCollabReportMutation
                .mutateAsync(document.entityGuid!)
                .then(handleDeleteSuccess)
                .catch((error) => {
                    showErrorToast(error.message ?? 'Failed to delete report');
                    setDocumentToDelete(null);
                });
        } else {
            deleteMutation
                .mutateAsync(document)
                .then(handleDeleteSuccess)
                .catch((error) => {
                    showErrorToast(error.message ?? 'Failed to delete document');
                    setDocumentToDelete(null);
                });
        }
    };

    const deleteMessage = (isCollab?: boolean, name?: string) => {
        const warning = 'This operation is permanent and cannot be undone.';
        if (isCollab === true) {
            return `Are you sure you want to delete the report titled '${name}'?\nAll documents/report data/alerts associated with this report will also be deleted.\n${warning}`;
        } else {
            return `Are you sure you want to delete the document titled '${name}'?\n${warning}`;
        }
    };

    // HANDLERS
    const handleToggleFilterPane = () => {
        setShowFilterPane((prev) => !prev);
    };

    const handleApplyFilters = (searchModel: IDocumentSearchModel) => {
        setDocumentSearchModel(searchModel);
    };

    const handleSort = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        setSort({ pageNumber, key, order, subKey, subGrandKey });
    };

    const handleDocumentDeleteClick = async (
        documentToDelete: IViewDocumentModel,
        onSuccess?: () => void
    ) => {
        setDocumentToDelete(documentToDelete);
        if (typeof onSuccess === 'function') {
            setDeleteCallback(() => onSuccess);
        }
    };

    const handleDocumentDownloadClick = async (
        event: React.FormEvent<HTMLButtonElement>,
        documentToDownload: IViewDocumentModel
    ) => {
        const { guid, filename } = documentToDownload;

        const handleDownloadSuccess = () => {
            setFilteredRows((prev) => {
                const prevCopy = cloneDeep(prev);
                const found = prevCopy.find((row) => row.guid === guid);

                if (found) {
                    found.isRead = true;
                }
                return prevCopy;
            });
            setDocumentsWithErrors((prev) => prev.filter((docGuid) => docGuid !== guid));
        };

        const handleDownloadError = (error: Error) => {
            setDocumentsWithErrors((prev) => [...prev, guid!]);
            setError(error.message);
        };

        await downloadDocument(event, guid!, filename!)
            .then(handleDownloadSuccess)
            .catch(handleDownloadError);
    };

    const handleDocumentViewClick = async (document: IViewDocumentModel) => {
        const documentsCopy = [...(cloneDeep(myDocumentsApi.data) ?? [])];
        const found = documentsCopy.find((doc) => doc.guid === document.guid);

        if (found) {
            found.isRead = true;
            queryClient.setQueryData(useGetMyDocumentsKey, documentsCopy);
        }
    };

    const handleNextPage = () => {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
    };

    const handlePreviousPage = () => {
        setPageNumber((prevPageNumber) => Math.max(prevPageNumber - 1, 0));
    };

    // EFFECTS
    useEffect(() => {
        const documentsCopy = [...(cloneDeep(myDocumentsApi.data) ?? [])];
        if (sort) {
            documentsCopy.sort(
                Sort.compareValues(sort.key, sort.subKey, sort.order, sort.subGrandKey)
            );
        }
        setFilteredRows(documentsCopy);
    }, [sort, myDocumentsApi.data]);

    useEffect(() => {
        if (!myDocumentsApi.isFetching && myDocumentsApi.isFetched) {
            setShowFilterPane(false);
        }
    }, [myDocumentsApi.isFetching, myDocumentsApi.isFetched]);

    return (
        <>
            <DocumentTitle title="My Documents" />
            <Offcanvas
                show={showFilterPane}
                onHide={() => setShowFilterPane(false)}
                placement="end"
                scroll
                backdrop
            >
                <Offcanvas.Header
                    closeButton
                    style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                >
                    <Offcanvas.Title>
                        <span style={{ fontSize: 20, fontWeight: 500 }}>{pageTitle} Filters</span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <MyDocumentsSearchFilters
                        currentFilters={documentSearchModel}
                        isSearching={myDocumentsApi.isFetching}
                        onApplyFilters={handleApplyFilters}
                    />
                </Offcanvas.Body>
            </Offcanvas>

            <PageWithAffixedHeader
                headerComponent={
                    <>
                        <h1>{pageTitle}</h1>
                        <MyDocumentsAppliedFilters
                            searchModel={documentSearchModel}
                            onApplyFilters={handleApplyFilters}
                            onToggleFilterPane={handleToggleFilterPane}
                        />
                        {myDocumentsApi.isError && (
                            <div className="row mb-1 me-2">
                                <Alert variant="error">{error}</Alert>
                            </div>
                        )}
                        {error && (
                            <div>
                                <span className="text-danger">{error}</span>
                            </div>
                        )}
                    </>
                }
            >
                {myDocumentsApi.isFetching && <Loader />}
                <div className="pb-3">
                    <Table responsive size="sm">
                        <thead>
                            <DocumentsHeader onSort={handleSort} />
                        </thead>
                        <tbody>
                            {filteredRows.map((item: IViewDocumentModel) => (
                                <DocumentRow
                                    key={item.guid}
                                    document={item}
                                    documentsDownloading={documentsDownloading}
                                    documentsWithErrors={documentsWithErrors}
                                    documentTypes={documentTypesLookup.data ?? []}
                                    canDelete={true}
                                    onDeleteClick={handleDocumentDeleteClick}
                                    onDownloadClick={handleDocumentDownloadClick}
                                    onViewClick={handleDocumentViewClick}
                                />
                            ))}
                        </tbody>
                    </Table>
                    {myDocumentsApi.isSuccess && (
                        <Pagination
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            count={documentsCountResponse?.data ?? 0}
                            handleNextPage={handleNextPage}
                            handlePreviousPage={handlePreviousPage}
                        />
                    )}
                </div>
            </PageWithAffixedHeader>

            {documentToDelete && (
                <CheckmateDialog
                    isShowingModal
                    body={deleteMessage(documentToDelete.isCollab, documentToDelete.filename)}
                    handleClose={() => setDocumentToDelete(null)}
                    handleConfirm={() => executeDelete(documentToDelete)}
                    confirmText="Delete"
                    cancelText="Cancel"
                    confirmButtonClassName="btn btn-danger"
                    closeButtonClassName="btn btn-default"
                    title="Delete Document"
                    showBackdrop
                />
            )}
        </>
    );
}
